"use client";

/**
 * Keys whose latest value never reached `localStorage` (quota exceeded, storage
 * blocked in private mode). The browser can still hold an *older* value for
 * such a key, and that value would otherwise win over the in-memory copy and
 * silently revert the write — so reads skip `localStorage` until a later write
 * succeeds. The trade-off: while a key is listed here, a successful write from
 * another tab stays invisible to this one.
 */
const unpersistedKeys = new Set<string>();

export const getStorageItem = (key: string): string => {
    if (typeof window === "undefined") return "";

    const storage: Record<string, string | undefined> = window.InnerBalanceClientApi?.["Storage"] || {};

    if (unpersistedKeys.has(key)) return storage[key] ?? "";

    try {
        return localStorage.getItem(key) ?? storage[key] ?? "";
    } catch(e) {
        console.warn(e);
    }

    return storage[key] ?? "";
};

export type StorageWriteResult = {
    /**
     * Whether the value reached `localStorage`. The in-memory fallback is
     * always written, so `false` means the value survives this page load only.
     */
    persisted: boolean;
    error?: unknown;
};

export const setStorageItem = (key: string, value: string): StorageWriteResult => {
    window.InnerBalanceClientApi = window.InnerBalanceClientApi || {};
    window.InnerBalanceClientApi["Storage"] = window.InnerBalanceClientApi["Storage"] || {};
    window.InnerBalanceClientApi["Storage"][key] = value;

    try {
        localStorage.setItem(key, value);
        unpersistedKeys.delete(key);
        return { persisted: true };
    } catch(e) {
        unpersistedKeys.add(key);
        console.warn(e);
        return { persisted: false, error: e };
    }
};

export const removeStorageItem = (key: string) => {
    window.InnerBalanceClientApi = window.InnerBalanceClientApi || {};
    window.InnerBalanceClientApi["Storage"] = window.InnerBalanceClientApi["Storage"] || {};
    delete window.InnerBalanceClientApi["Storage"][key];
    unpersistedKeys.delete(key);

    try {
        localStorage.removeItem(key);
    } catch(e) {
        console.warn(e);
    }
};
